import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { getNewsletterAdmin, previewNewsletter, sendNewslettersNow, setNewsletterSource, updateNewsletterSettings, type NewsletterAdminView, type NewsletterPreviewItem, type NewsletterSettings } from '../../../api/newsletterAdminApi';
import { S, fmtWhen } from './adminPageStyles';

/** Newsletter administration (SRS #29): sources, blocked keywords, digest size, send hour, editor's note, frequencies, subscribers. */

const AdminNewsletterPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const [view, setView] = useState<NewsletterAdminView | null>(null);
  const [form, setForm] = useState<NewsletterSettings | null>(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [previewTopic, setPreviewTopic] = useState('');
  const [previewCountry, setPreviewCountry] = useState('');
  const [preview, setPreview] = useState<{ pool: number; items: NewsletterPreviewItem[] } | null>(null);

  const load = useCallback(() => {
    getNewsletterAdmin().then((v) => { setView(v); setForm(v.settings); }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form) return;
    setError(null); setOk(null);
    try {
      const r = await updateNewsletterSettings(form);
      setForm(r.settings); setOk('Newsletter settings saved.'); load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to save'); }
  };
  const toggleSource = async (key: string, enabled: boolean) => {
    try { await setNewsletterSource(key, enabled); load(); } catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  };
  const sendNow = async () => {
    if (!window.confirm('Send every digest that is due today now, regardless of the send hour?')) return;
    try { const r = await sendNewslettersNow(); setOk(`Sent ${r.sent}, skipped ${r.skipped}, errors ${r.errors}.`); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  };
  const runPreview = async () => {
    try { setPreview(await previewNewsletter({ topics: previewTopic ? [previewTopic] : [], countries: previewCountry ? [previewCountry] : [] })); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed'); }
  };
  const addKeyword = () => {
    const k = keywordInput.trim().toLowerCase();
    if (!k || !form) return;
    setForm({ ...form, blocked_keywords: Array.from(new Set([...form.blocked_keywords, k])) });
    setKeywordInput('');
  };

  const tile = (l: string, v: React.ReactNode) => (
    <div key={l} style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ds-text)' }}>{v}</div>
      <div style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>{l}</div>
    </div>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Newsletter" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <h2 style={S.h2}>Newsletter</h2>
        <p style={S.sub}>Central control of the AI-policy newsletter: which sources feed it, keywords that never make it in, how big a digest is, when it goes out, and an editor's note. Readers pick their own countries, topics and frequency; these settings apply on top.</p>
        {error && <div style={S.error}>{error}</div>}
        {ok && <div style={S.ok}>{ok}</div>}

        {view && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
            {tile('Daily subscribers', view.stats.subscribers.daily)}
            {tile('Weekly subscribers', view.stats.subscribers.weekly)}
            {tile('Monthly subscribers', view.stats.subscribers.monthly)}
            {tile('Sent today', view.stats.sent_today)}
            {tile('Sent last 7 days', view.stats.sent_last_7_days)}
          </div>
        )}

        {form && (
          <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: 14, marginBottom: 16, display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
              <label style={{ fontSize: 12.5 }}>Items per digest{' '}
                <input aria-label="Items per digest" type="number" min={1} max={25} style={{ ...S.input, minWidth: 70 }} value={form.max_items} onChange={(e) => setForm({ ...form, max_items: Number(e.target.value) })} />
              </label>
              <label style={{ fontSize: 12.5 }}>Send hour (UTC){' '}
                <select aria-label="Send hour (UTC)" style={{ ...S.input, minWidth: 80 }} value={form.send_hour_utc} onChange={(e) => setForm({ ...form, send_hour_utc: Number(e.target.value) })}>
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                </select>
              </label>
              <span style={{ fontSize: 12.5 }}>Frequencies offered{' '}
                {(['daily', 'weekly', 'monthly'] as const).map((f) => (
                  <label key={f} style={{ marginLeft: 8 }}>
                    <input type="checkbox" checked={form.frequencies_enabled.includes(f)}
                      onChange={(e) => setForm({ ...form, frequencies_enabled: e.target.checked ? [...form.frequencies_enabled, f] : form.frequencies_enabled.filter((x) => x !== f) })} /> {f}
                  </label>
                ))}
              </span>
            </div>
            <label style={{ fontSize: 12.5, display: 'grid', gap: 4 }}>Editor's note (top of every digest)
              <textarea aria-label="Editor's note" style={{ ...S.input, minHeight: 60, fontFamily: 'inherit' }} value={form.intro_note} maxLength={1000} onChange={(e) => setForm({ ...form, intro_note: e.target.value })} />
            </label>
            <div>
              <div style={{ fontSize: 12.5, marginBottom: 4 }}>Blocked keywords — a title containing one never appears in the feed or a digest</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {form.blocked_keywords.map((k) => (
                  <span key={k} style={S.chip}>{k} <button aria-label={`Remove ${k}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ds-text-muted)' }} onClick={() => setForm({ ...form, blocked_keywords: form.blocked_keywords.filter((x) => x !== k) })}>×</button></span>
                ))}
                <input aria-label="Add blocked keyword" style={{ ...S.input, minWidth: 140 }} placeholder="e.g. crypto" value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }} />
                <button style={S.btn} onClick={addKeyword}>Add</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.primary} onClick={save}>Save settings</button>
              <button style={S.btn} onClick={sendNow}>Send due digests now</button>
            </div>
          </div>
        )}

        {view && (
          <>
            <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Sources (last 30 days)</h3>
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={S.table}>
                <thead><tr><th style={S.th}>Source</th><th style={S.th}>Country</th><th style={S.th}>Articles</th><th style={S.th}>Last article</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
                <tbody>
                  {view.sources.map((s) => (
                    <tr key={s.source_key}>
                      <td style={S.td}>{s.name} <span style={{ ...S.mono, ...S.muted }}>{s.source_key}</span></td>
                      <td style={S.td}>{s.country}</td>
                      <td style={S.td}>{s.articles}</td>
                      <td style={S.td}>{s.last_seen ? fmtWhen(s.last_seen) : <span style={S.muted}>never</span>}</td>
                      <td style={S.td}><span style={{ ...S.chip, background: s.enabled ? 'var(--ds-success-bg)' : 'var(--ds-danger-bg)', color: s.enabled ? 'var(--ds-success)' : 'var(--ds-danger)' }}>{s.enabled ? 'on' : 'off'}</span></td>
                      <td style={S.td}><button style={S.btn} onClick={() => toggleSource(s.source_key, !s.enabled)}>{s.enabled ? 'Switch off' : 'Switch on'}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={{ fontSize: 15, margin: '0 0 8px' }}>Preview a digest</h3>
            <div style={S.toolbar}>
              <select aria-label="Preview topic" style={S.input} value={previewTopic} onChange={(e) => setPreviewTopic(e.target.value)}>
                <option value="">Any topic</option>
                {view.topics.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <select aria-label="Preview country" style={S.input} value={previewCountry} onChange={(e) => setPreviewCountry(e.target.value)}>
                <option value="">Any country</option>
                {Array.from(new Set(view.sources.map((s) => s.country))).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button style={S.btn} onClick={runPreview}>Preview</button>
              {preview && <span style={S.muted}>{preview.pool} eligible articles this week</span>}
            </div>
            {preview && (
              <ol style={{ paddingLeft: 18, fontSize: 13.5 }}>
                {preview.items.map((it) => (
                  <li key={it.url} style={{ marginBottom: 6 }}>
                    <a href={it.url} target="_blank" rel="noreferrer">{it.title}</a>
                    <span style={S.muted}> — {it.source_name} · {it.country} · score {it.score}{it.topics.length ? ` · ${it.topics.join(', ')}` : ''}</span>
                  </li>
                ))}
                {preview.items.length === 0 && <li style={S.muted}>Nothing eligible — check the sources and blocked keywords.</li>}
              </ol>
            )}
          </>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminNewsletterPage;
